import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Stethoscope, Phone } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { Cloud, Sparkle, Wave } from "./Decor";
import { WhatsAppButton } from "./WhatsApp";

const ease = [0.22, 1, 0.36, 1];

const Badge = ({ text, className, testId }) => (
  <div data-testid={testId} className={`flex-wrap items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-pink-600 shadow-sm mb-5 ${className}`}>
    <Stethoscope className="h-4 w-4" />
    {text}
  </div>
);

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay, ease }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ c }) => {
  const p = c?.profile || {};
  const clinic = c?.clinic || {};
  const s = c?.settings || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const teddyY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} id="home" className="relative overflow-hidden bg-gradient-to-b from-sky-100 via-sky-50 to-white" data-testid="hero-section">
      <Cloud className="drift absolute left-[-40px] top-10 w-40 text-white sm:w-56" />
      <Cloud className="drift absolute right-[-30px] top-24 w-32 text-white/90 sm:w-48" />
      <Sparkle className="absolute left-[45%] top-12 h-6 w-6 text-yellow-300" />
      <Sparkle className="absolute right-[12%] bottom-28 h-5 w-5 text-pink-300" />
      <Sparkle className="absolute left-[8%] bottom-40 h-4 w-4 text-orange-300" />
      <div className="relative mx-auto grid max-w-6xl gap-10 px-4 pb-24 pt-10 sm:px-6 md:grid-cols-[1.1fr_0.9fr] md:gap-x-10 md:gap-y-0 md:pt-16 lg:gap-x-16">
        <div className="md:col-start-1 md:row-start-1 md:self-end" data-testid="hero-name-block">
          {p.designation && <Badge text={p.designation} testId="hero-designation" className="hidden md:inline-flex" />}
          <h1 data-testid="hero-doctor-name" className="font-display text-4xl font-semibold leading-[1.1] text-blue-900 sm:text-5xl lg:text-6xl">
            <MaskLine delay={0.1}>{p.name}</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.45, ease }} data-testid="hero-degrees" className="mt-3 text-base font-semibold text-slate-700 sm:text-lg">
            {p.degrees}
          </motion.p>
          {p.designation_local && <p className="mt-1 text-sm font-medium text-pink-600">{p.designation_local}</p>}
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6, ease }} className="md:col-start-1 md:row-start-2 md:self-start">
          {p.designation && <Badge text={p.designation} testId="hero-designation-mobile" className="inline-flex md:hidden" />}
          <div className="md:mt-6 rounded-3xl bg-white/80 p-5 shadow-sm ring-1 ring-sky-100">
            <p className="font-display text-xl font-medium text-orange-500">{p.welcome_title}</p>
            <p data-testid="hero-welcome-message" className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">{p.welcome_message}</p>
          </div>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <WhatsAppButton wa={c?.whatsapp} testId="hero-whatsapp-btn" className="text-base sm:text-lg" />
            {clinic.phone && (
              <a href={`tel:${clinic.phone}`} data-testid="hero-call-btn" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-blue-900/15 bg-white px-6 py-3.5 font-semibold text-blue-900 transition-colors hover:bg-sky-50">
                <Phone className="h-4 w-4" /> {s.call_button_prefix || "Call"} {clinic.phone}
              </a>
            )}
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.94, rotate: -2 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1, delay: 0.2, ease }}
          className="relative row-start-2 mx-auto w-full max-w-[290px] self-center sm:max-w-sm md:col-start-2 md:row-span-2 md:row-start-1 md:max-w-none"
        >
          <div className="absolute -inset-3 rotate-3 rounded-[3rem] bg-pink-100" />
          <div className="absolute -inset-3 -rotate-2 rounded-[3rem] bg-yellow-100" />
          <div className="relative overflow-hidden rounded-[2.75rem] border-[6px] border-white bg-white shadow-xl">
            {p.photo && (
              <motion.img style={{ y: photoY, scale: 1.08 }} src={imgUrl(p.photo)} alt={p.name} data-testid="hero-doctor-photo" className="aspect-[4/4.5] w-full object-cover object-top" />
            )}
          </div>
          {s.hero_decor_image && (
            <motion.div style={{ y: teddyY }} className="absolute -bottom-8 -left-6 w-24 sm:w-32">
              <img src={imgUrl(s.hero_decor_image)} alt="" aria-hidden="true" className="floaty w-full" />
            </motion.div>
          )}
        </motion.div>
      </div>
      <Wave />
    </section>
  );
};
